import { Group, SegmentedControl, Select } from "@mantine/core";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CustomerFilter, CustomerObject } from "@msc/shared";

interface Props {
  filter: CustomerFilter;
  onFilterChange: (f: CustomerFilter) => void;
  customers: CustomerObject[];
  isLoading: boolean;
  isError: boolean;
  selectedKey: string | null;
  onSelect: (objectKey: string) => void;
  /**
   * Gesperrt, solange eine Präsentation läuft (Phase != "idle"). Ein
   * Kundenwechsel mitten im Termin zöge dem Deck die Tickets unter den Füßen
   * weg — der Store hängt am Kunden, die Bühne zeigt aber weiter das alte
   * Deck. Deshalb sperren statt auszublenden: Der Lead soll sehen, bei wem er
   * ist, und den Grund erfahren (`title`).
   */
  disabled?: boolean;
}

/** Meine/Alle, Bereich (grenzt nur die Auswahl ein), durchsuchbare Kundenliste. */
export function CustomerPicker({ filter, onFilterChange, customers, isLoading, isError, selectedKey, onSelect, disabled = false }: Props) {
  const { t } = useTranslation();
  const [bereich, setBereich] = useState<string | null>(null);
  const lockedTitle = disabled ? t("pages.customerView.present.pickerLocked") : undefined;

  const bereiche = useMemo(
    () => [...new Set(customers.map((c) => c.bereich?.name).filter((n): n is string => !!n))].sort((a, b) => a.localeCompare(b, "de")),
    [customers],
  );
  const options = useMemo(
    () =>
      customers
        .filter((c) => !bereich || c.bereich?.name === bereich)
        .map((c) => ({ value: c.objectKey, label: `${c.name} (${c.objectKey})` })),
    [customers, bereich],
  );

  return (
    <Group gap="sm" wrap="wrap" title={lockedTitle}>
      <SegmentedControl
        size="xs"
        disabled={disabled}
        value={filter}
        onChange={(v) => { onFilterChange(v as CustomerFilter); setBereich(null); }}
        data={[
          { value: "mine", label: t("pages.customerView.cockpit.mine") },
          { value: "all", label: t("pages.customerView.cockpit.all") },
        ]}
      />
      {bereiche.length > 0 && (
        <Select
          size="xs"
          w={180}
          clearable
          disabled={disabled}
          title={lockedTitle}
          placeholder={t("pages.customerView.cockpit.allAreas")}
          value={bereich}
          onChange={setBereich}
          data={bereiche}
        />
      )}
      <Select
        size="xs"
        w={320}
        searchable
        disabled={disabled}
        title={lockedTitle}
        nothingFoundMessage={isLoading ? t("common.loading") : t("pages.customerView.cockpit.noMatch")}
        placeholder={t("pages.customerView.cockpit.pickCustomer")}
        value={selectedKey}
        onChange={(v) => { if (v) onSelect(v); }}
        data={options}
        error={isError ? t("pages.customerView.cockpit.customersError") : undefined}
      />
    </Group>
  );
}
